import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../hooks/useTranslation";
import { useErrorNotification } from "../../hooks/useErrorNotification";
import ErrorNotification from "../shared/ErrorNotification";
import { MEDIA_URL } from '../../config/constants';
import { recordStep2Completion } from "../../services/cartSessionService";
import { trackAddPaymentInfo } from "../../services/analyticsService";
import { submitBookingAndRedirect } from "../../services/checkoutPayment";

const InsuranceSelector = () => {
  const navigate = useNavigate();
  const { t, getLocalizedPath } = useTranslation('checkout');
  const { showError, hideError, error } = useErrorNotification();
  const [selectedPlan, setSelectedPlan] = useState("premium");
  const [expandedPlan, setExpandedPlan] = useState("premium");
  // Three years at the wheel is a minimum to rent at all, not the point at
  // which Ultimate becomes compulsory. Under it, the booking is refused.
  const [isUnderExperienceMinimum, setIsUnderExperienceMinimum] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  // How much to pay online: "deposit" (50% of the rental) or "full" (everything
  // including add-ons). Mirrored into sessionStorage so BookingSummary can show
  // the matching split.
  const [paymentOption, setPaymentOption] = useState(
    () => sessionStorage.getItem("checkoutPaymentOption") || "deposit"
  );

  const choosePaymentOption = (option) => {
    setPaymentOption(option);
    sessionStorage.setItem("checkoutPaymentOption", option);
    window.dispatchEvent(new Event("bookingUpdated"));
  };

  // Calculate driving experience from license start date and ensure premium is pre-selected
  useEffect(() => {
    const step1Data = sessionStorage.getItem("checkoutStep1Data");
    if (step1Data) {
      try {
        const parsed = JSON.parse(step1Data);
        if (parsed.licenseStartDate) {
          const licenseDate = new Date(parsed.licenseStartDate);
          const today = new Date();
          const diffInYears = (today - licenseDate) / (365.25 * 24 * 60 * 60 * 1000);

          if (diffInYears < 3) {
            setIsUnderExperienceMinimum(true);
          }
        }
      } catch (e) {
        console.error("Error parsing step1 data:", e);
      }
    }

    // IMPORTANT: Save the default premium selection to sessionStorage on mount
    // This ensures the insurance shows up in BookingSummary even if user doesn't interact
    const existingStep2Data = sessionStorage.getItem("checkoutStep2Data");
    if (!existingStep2Data) {
      const insuranceData = { selectedPlan: "premium" };
      sessionStorage.setItem("checkoutStep2Data", JSON.stringify(insuranceData));
      window.dispatchEvent(new Event("bookingUpdated"));
    }
  }, []);

  const plans = [
    {
      id: "basic",
      name: t('step2.plans.basic.name'),
      price: 0,
      deposit: 1600,
      description: t('step2.plans.basic.description'),
      includes: t('step2.plans.basic.includes'),
      isIncluded: true,
    },
    {
      id: "premium",
      name: t('step2.plans.premium.name'),
      price: 17,
      deposit: 900,
      description: t('step2.plans.premium.description'),
      includes: t('step2.plans.premium.includes'),
      isMostPicked: true,
    },
    {
      id: "ultimate",
      name: t('step2.plans.ultimate.name'),
      price: 23,
      deposit: 350,
      description: t('step2.plans.ultimate.description'),
      includes: t('step2.plans.ultimate.includes'),
    },
  ];

  const handlePlanSelect = (planId) => {
    if (isUnderExperienceMinimum) return;

    setSelectedPlan(planId);

    // Update sessionStorage and trigger booking update event
    const insuranceData = { selectedPlan: planId };
    sessionStorage.setItem("checkoutStep2Data", JSON.stringify(insuranceData));
    window.dispatchEvent(new Event("bookingUpdated"));
  };

  const toggleDetails = (planId) => {
    setExpandedPlan(expandedPlan === planId ? null : planId);
  };

  // Insurance is now the FINAL step — this button creates the order and
  // redirects to Stripe for the 50% deposit.
  const handlePayDeposit = async () => {
    if (isProcessingPayment) return;
    // The insurer will not cover a main driver with under three years'
    // experience, so the booking cannot be taken at all.
    if (isUnderExperienceMinimum) {
      showError(t('step2.experienceMinimumBody'));
      return;
    }

    // Store selected insurance plan in sessionStorage before submitting
    sessionStorage.setItem("checkoutStep2Data", JSON.stringify({ selectedPlan }));

    // Analytics: Record step 2 completion (non-blocking)
    try {
      recordStep2Completion({ selectedPlan });
      const bookingData = JSON.parse(sessionStorage.getItem("bookingData") || "{}");
      trackAddPaymentInfo(bookingData, bookingData?.totalPrice);
    } catch (err) {
      // Analytics errors should never block checkout
    }

    setIsProcessingPayment(true);
    try {
      await submitBookingAndRedirect({ t, showError, paymentOption });
    } catch (err) {
      console.error("Error proceeding to payment:", err);
      if (err?.message) showError(err.message);
      setIsProcessingPayment(false);
    }
  };

  const handleTermsClick = () => {
    navigate(getLocalizedPath("/terms-and-conditions"));
  };

  return (
    <div className="insurance-selector">
      {error && (
        <ErrorNotification message={error} onDismiss={hideError} />
      )}
      <div className="insurance-header">
        <h1 className="insurance-title">{t('step2.title')}</h1>
        <p className="insurance-description">
          {t('step2.subtitle')}
        </p>
      </div>

      <div className="insurance-form-container">
        <h2 className="insurance-form-title">
          {t('step2.formTitle')}
        </h2>

        {isUnderExperienceMinimum && (
          <div className="insurance-experience-block" role="alert">
            <strong>{t('step2.experienceMinimumTitle')}</strong>
            <p>{t('step2.experienceMinimumBody')}</p>
          </div>
        )}

        <div className="insurance-content">
          <div className="insurance-plans-list">
            {plans.map((plan) => {
              const isDisabled = isUnderExperienceMinimum;
              return (
                <div
                  key={plan.id}
                  className={`insurance-plan-item ${selectedPlan === plan.id ? "selected" : ""} ${isDisabled ? "disabled" : ""}`}
                >
                {plan.isMostPicked && (
                  <div className="most-picked-banner">
                    {t('step2.mostPicked')}
                  </div>
                )}
                <div className="insurance-plan-header">
                  <div
                    className="insurance-plan-checkbox"
                    onClick={() => !isDisabled && handlePlanSelect(plan.id)}
                    style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                  >
                    <div className="insurance-checkbox-container">
                      <div className="insurance-checkbox-state-layer">
                        <div className="insurance-checkbox-border"></div>
                        {selectedPlan === plan.id && (
                          <div className="insurance-checkbox-check">
                            <svg
                              width="8"
                              height="8"
                              viewBox="0 0 8 8"
                              fill="none"
                            >
                              <circle
                                cx="4"
                                cy="4"
                                r="4"
                                fill="#009B31"
                              ></circle>
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="insurance-plan-name-group">
                      <div className="insurance-plan-name">{plan.name}</div>
                      <div className="insurance-plan-deposit-inline">
                        {t('step2.securityDeposit')}: <strong>€{plan.deposit}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="insurance-plan-price">
                    {plan.isIncluded ? t('step2.included') : `€${plan.price} ${t('step2.perDay')}`}
                  </div>
                </div>
                <div className="insurance-plan-description">
                  {plan.description}
                </div>

                {plan.includes && expandedPlan === plan.id && (
                  <div className="insurance-plan-includes">
                    <div className="insurance-includes-title">
                      {t('step2.whatsIncluded')}
                    </div>
                    <div className="insurance-includes-list">
                      {plan.includes.split('\n').map((line, i) => {
                        const isHeader = line.trim().endsWith(':');
                        return (
                          <div
                            key={i}
                            className={`insurance-include-item${isHeader ? ' is-header' : ''}`}
                          >
                            {line}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div
                  className="insurance-plan-toggle"
                  onClick={() => toggleDetails(plan.id)}
                >
                  <div className="insurance-toggle-text">
                    {expandedPlan === plan.id ? t('step2.hideDetails') : t('step2.showDetails')}
                  </div>
                  <div className="insurance-toggle-icon">
                    {expandedPlan === plan.id ? (
                      <img
                        src={`${MEDIA_URL}/icons/navigation/chevron-down.svg`}
                        alt="chevron-down"
                        className="insurance-chevron"
                      />
                    ) : (
                      <svg
                        width="16"
                        height="17"
                        viewBox="0 0 16 17"
                        fill="none"
                      >
                        <path
                          d="M4 6.5L8 10.5L12 6.5"
                          stroke="#009B31"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </div>
                
                {isDisabled && (
                  <div className="license-restriction-notice">
                    <small style={{ color: "#ff6b6b", fontStyle: "italic" }}>
                      {t('step2.experienceMinimumShort')}
                    </small>
                  </div>
                )}
              </div>
              );
            })}
          </div>

          <div className="insurance-terms">
            {t('step2.termsText')}{" "}
            <span
              className="insurance-terms-link"
              onClick={handleTermsClick}
              style={{ cursor: 'pointer' }}
            >
              {t('step2.termsLink')}
            </span>.
          </div>
        </div>

        <div className="payment-option-selector">
          <div className="payment-option-title">{t('step2.paymentOption.title')}</div>
          <div className="payment-option-choices">
            {[
              { id: 'deposit', label: t('step2.paymentOption.deposit'), hint: t('step2.paymentOption.depositHint') },
              { id: 'full', label: t('step2.paymentOption.full'), hint: t('step2.paymentOption.fullHint') },
            ].map((option) => (
              <button
                type="button"
                key={option.id}
                className={`payment-option-card${paymentOption === option.id ? ' selected' : ''}`}
                onClick={() => choosePaymentOption(option.id)}
                aria-pressed={paymentOption === option.id}
              >
                <span className="payment-option-label">{option.label}</span>
                <span className="payment-option-hint">{option.hint}</span>
              </button>
            ))}
          </div>
        </div>

        <button
          className="insurance-continue-button"
          onClick={handlePayDeposit}
          disabled={isProcessingPayment || isUnderExperienceMinimum}
        >
          {isProcessingPayment
            ? (t('step3.processing') || 'Processing...')
            : t(paymentOption === 'full' ? 'step3.proceedToPaymentFull' : 'step3.proceedToPayment')}
        </button>
      </div>
    </div>
  );
};

export default InsuranceSelector;
